// «Προκαταβολή πελάτη» στην πληρωμή του Take Away (απόφαση χρήστη 14/10/2026). Ο ταμίας βρίσκει τις
// ανοιχτές προκαταβολές του πελάτη με ΟΛΟ το τηλέφωνο (ποτέ με κομμάτι — λάθος πελάτης = λάθος χρήματα),
// διαλέγει μία ή περισσότερες του ΙΔΙΟΥ πελάτη και πόσα αφαιρούνται από την καθεμία: όχι πάνω από το
// διαθέσιμο, ούτε όλες μαζί πάνω από το σύνολο. Εδώ δεν γράφεται τίποτα — η αφαίρεση φεύγει μαζί με την
// παραγγελία (depositUses) και ο server ξαναελέγχει τα πάντα.
//
// Αριθμητικό στο ίδιο το παράθυρο (οθόνες αφής χωρίς πληκτρολόγιο): γράφει στο πεδίο που πατήθηκε τελευταίο
// — τηλέφωνο ή ποσό μιας επιλεγμένης προκαταβολής.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, Delete, Search } from 'lucide-react';
import { Btn, Note, inputClass } from '../dineIn/floorEditor/ui';
import { athensDateTime } from '../register/registerFormat';
import { DepositApiError, depositsApi } from './api';
import {
  MAX_DEPOSIT_USES, capFor, centsToInput, digits, docNumber, money, parseAmount, toCents,
} from './model';
import { Shell, useDT } from './ui';
import type { TakeAwayDepositSelection } from './useTakeAwayDeposit';
import type { CustomerDeposit } from './types';

/** Μία επιλεγμένη προκαταβολή: το κείμενο του πεδίου ποσού (όπως το γράφει ο ταμίας, «12,50»). */
type Picked = { id: number; text: string };
type Active = 'phone' | number;

const MAX_PHONE = 15;

/** Μόνο ψηφία και ένα υποδιαστολή, έως 2 δεκαδικά. */
function cleanAmount(raw: string): string {
  let s = String(raw || '').replace(/[^\d.,]/g, '').replace('.', ',');
  const i = s.indexOf(',');
  if (i >= 0) s = s.slice(0, i + 1) + s.slice(i + 1).replace(/[.,]/g, '').slice(0, 2);
  return s;
}

/** «12,» ή κενό = ο ταμίας γράφει ακόμη· όχι σφάλμα. */
const typing = (text: string) => text === '' || /^\d+,$/.test(text);

export default function TakeAwayDepositSheet({ storeId, totalCents, initial, onApply, onRemove, onClose }: {
  storeId: number;
  /** Το σύνολο της παραγγελίας σε λεπτά. */
  totalCents: number;
  /** Η τρέχουσα επιλογή (αλλαγή): ξαναβρίσκει τον πελάτη και κρατά τα ποσά. */
  initial?: TakeAwayDepositSelection | null;
  onApply: (selection: TakeAwayDepositSelection) => void;
  /** «Χωρίς προκαταβολή» — μόνο όταν υπάρχει ήδη επιλογή. */
  onRemove?: () => void;
  onClose: () => void;
}) {
  const dt = useDT();
  const [phone, setPhone] = useState(initial?.customer.phone || '');
  const [found, setFound] = useState<{ phone: string; list: CustomerDeposit[] } | null>(null);
  const [searched, setSearched] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [picked, setPicked] = useState<Picked[]>(() => (initial ? initial.uses.map(u => ({ id: u.paymentId, text: centsToInput(u.amountCents) })) : []));
  const [active, setActive] = useState<Active>('phone');
  const seq = useRef(0);

  const phoneDigits = digits(phone);
  const canSearch = phoneDigits.length >= 10;

  const search = useCallback(async (d: string) => {
    if (d.length < 10) return;
    const my = ++seq.current;
    setSearched(d);
    setLoading(true);
    setError(null);
    try {
      const list = await depositsApi.customerDeposits(storeId, d);
      if (my !== seq.current) return;
      setFound({ phone: d, list });
      // Μένουν επιλεγμένες μόνο όσες ήρθαν ξανά και αφαιρούνται τώρα.
      setPicked(prev => prev.filter(p => list.some(x => x.id === p.id && x.usable)));
    } catch (e) {
      if (my !== seq.current) return;
      setFound(null);
      const code = e instanceof DepositApiError ? e.code : null;
      const byCode = code ? dt(`deposits:code.${code}`, { defaultValue: '' }) : '';
      setError((e instanceof DepositApiError && e.message) || byCode || dt('deposits:takeaway.loadFailed'));
    } finally {
      if (my === seq.current) setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  // Αναζήτηση μόνη της μόλις γραφτεί όλο το τηλέφωνο (μία φορά ανά τηλέφωνο· «Αναζήτηση» για ξανά).
  useEffect(() => {
    if (!canSearch || phoneDigits === searched) return undefined;
    const id = window.setTimeout(() => { void search(phoneDigits); }, 300);
    return () => window.clearTimeout(id);
  }, [canSearch, phoneDigits, searched, search]);

  const changePhone = (next: string) => {
    const clean = next.replace(/[^\d+\s]/g, '').slice(0, MAX_PHONE + 3);
    if (digits(clean).length > MAX_PHONE) return;
    setPhone(clean);
    // Άλλο τηλέφωνο = άλλος πελάτης: τίποτα από τα προηγούμενα.
    if (found && digits(clean) !== found.phone) {
      setFound(null);
      setPicked([]);
      setSearched('');
    }
  };

  const list = useMemo(() => found?.list || [], [found]);
  const byId = useMemo(() => new Map(list.map(d => [d.id, d])), [list]);
  const customers = useMemo(() => {
    const ids: number[] = [];
    for (const d of list) if (!ids.includes(d.customer.id)) ids.push(d.customer.id);
    return ids;
  }, [list]);

  const rows = picked.map(p => ({ p, d: byId.get(p.id) })).filter((x): x is { p: Picked; d: CustomerDeposit } => !!x.d);
  const centsOf = (p: Picked) => parseAmount(p.text.replace(',', '.'));
  const othersCents = (id: number) => rows.reduce((s, r) => (r.p.id === id ? s : s + Math.max(0, centsOf(r.p) || 0)), 0);
  const capOf = (d: CustomerDeposit) => capFor(toCents(d.available), totalCents, othersCents(d.id));

  const appliedCents = rows.reduce((s, r) => s + Math.max(0, centsOf(r.p) || 0), 0);
  const dueCents = Math.max(0, totalCents - appliedCents);
  const rowError = (r: { p: Picked; d: CustomerDeposit }) => {
    const c = centsOf(r.p);
    if (c == null || c <= 0) return typing(r.p.text) && r.p.id === active ? null : dt('deposits:err.amount');
    if (c > capOf(r.d)) return dt('deposits:takeaway.max', { amount: money(capOf(r.d) / 100) });
    return null;
  };
  const valid = rows.length > 0 && rows.every(r => {
    const c = centsOf(r.p);
    return c != null && c > 0 && c <= capOf(r.d);
  });
  const selectedCustomer = rows.length ? rows[0].d.customer : null;

  /** Ποτέ πάνω από το διαθέσιμο ή το σύνολο: ό,τι το ξεπερνά γίνεται αμέσως το μέγιστο. */
  const setAmount = (id: number, raw: string) => {
    setPicked(prev => prev.map(p => {
      if (p.id !== id) return p;
      const d = byId.get(id);
      const text = cleanAmount(raw);
      const c = parseAmount(text.replace(',', '.'));
      if (d && c != null) {
        const others = prev.reduce((s, q) => (q.id === id ? s : s + Math.max(0, parseAmount(q.text.replace(',', '.')) || 0)), 0);
        const cap = capFor(toCents(d.available), totalCents, others);
        if (c > cap) return { ...p, text: centsToInput(cap) };
      }
      return { ...p, text };
    }));
  };

  const toggle = (d: CustomerDeposit) => {
    if (!d.usable) return;
    const on = picked.some(p => p.id === d.id);
    if (on) {
      setPicked(prev => prev.filter(p => p.id !== d.id));
      if (active === d.id) setActive('phone');
      return;
    }
    // Μόνο ο ίδιος πελάτης σε μία παραγγελία: προκαταβολή άλλου πελάτη = νέα επιλογή.
    const same = rows.filter(r => r.d.customer.id === d.customer.id);
    if (same.length >= MAX_DEPOSIT_USES) return;
    const others = same.reduce((s, r) => s + Math.max(0, centsOf(r.p) || 0), 0);
    const cap = capFor(toCents(d.available), totalCents, others);
    if (cap <= 0) return;
    setPicked([...same.map(r => r.p), { id: d.id, text: centsToInput(cap) }]);
    setActive(d.id);
  };

  /** Το αριθμητικό γράφει στο πεδίο που πατήθηκε τελευταίο. */
  const press = (key: string) => {
    if (active === 'phone') {
      if (key === 'back') changePhone(phone.slice(0, -1));
      else if (key === 'clear') changePhone('');
      else if (/^\d$/.test(key)) changePhone(phone + key);
      return;
    }
    const p = picked.find(x => x.id === active);
    if (!p) return;
    if (key === 'back') setAmount(p.id, p.text.slice(0, -1));
    else if (key === 'clear') setAmount(p.id, '');
    else setAmount(p.id, p.text + key);
  };

  const apply = () => {
    if (!valid || !selectedCustomer) return;
    onApply({
      customer: selectedCustomer,
      uses: rows.map(r => ({ paymentId: r.d.id, amountCents: centsOf(r.p) as number, method: r.d.method })),
    });
  };

  const methodName = (d: CustomerDeposit) => dt(`deposits:method.${d.method}`);
  const keyClass = 'h-12 rounded-xl border border-line bg-surface-1 text-[18px] font-semibold text-t1 hover:bg-surface-2 active:scale-95 transition-all touch-manipulation';
  const activeLabel = active === 'phone' ? dt('deposits:takeaway.phone') : (() => {
    const d = byId.get(active);
    return d ? dt('deposits:takeaway.amountFor', { method: methodName(d), amount: money(d.available) }) : '';
  })();

  const footer = (
    <>
      {onRemove && initial && (
        <Btn variant="danger" className="mr-auto h-12" onClick={onRemove} data-testid="deposit-sheet-remove">{dt('deposits:takeaway.remove')}</Btn>
      )}
      <Btn className="h-12" onClick={onClose}>{dt('deposits:cancel')}</Btn>
      <Btn variant="primary" className="h-12 px-5 text-[15px]" disabled={!valid} onClick={apply} data-testid="deposit-sheet-apply">
        {appliedCents > 0 && valid ? dt('deposits:takeaway.apply', { amount: money(appliedCents / 100) }) : dt('deposits:takeaway.applyEmpty')}
      </Btn>
    </>
  );

  return (
    <Shell wide title={dt('deposits:takeaway.title')} sub={dt('deposits:takeaway.sub', { amount: money(totalCents / 100) })}
      closable onClose={onClose} footer={footer} testId="deposit-sheet">
      <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_232px]">
        <div className="grid min-w-0 content-start gap-3">
          <form className="flex items-end gap-2" onSubmit={e => { e.preventDefault(); if (canSearch) void search(phoneDigits); }}>
            <label className="grid min-w-0 flex-1 gap-1.5">
              <span className="text-[13px] font-medium text-t2">{dt('deposits:takeaway.phone')}</span>
              <input className={`${inputClass} h-12 text-[18px] font-semibold tabular-nums ${active === 'phone' ? 'border-blue-500' : ''}`}
                type="tel" inputMode="tel" autoComplete="off" value={phone} placeholder="69…" autoFocus
                onFocus={() => setActive('phone')} onChange={e => changePhone(e.target.value)} data-testid="deposit-sheet-phone"
                aria-describedby="deposit-sheet-phone-hint" />
            </label>
            <Btn type="submit" className="h-12" disabled={!canSearch || loading} icon={<Search className="h-4 w-4" />} data-testid="deposit-sheet-search">
              {dt('deposits:takeaway.search')}
            </Btn>
          </form>
          <span id="deposit-sheet-phone-hint" className="-mt-1.5 text-[12px] text-t3">{dt('deposits:takeaway.phoneHint')}</span>

          {error && <div data-testid="deposit-sheet-error"><Note tone="error">{error}</Note></div>}
          {loading && !found && (
            <div role="status" aria-live="polite" className="grid gap-2">
              <span className="sr-only">{dt('deposits:takeaway.searching')}</span>
              {[0, 1].map(i => <div key={i} className="h-16 animate-pulse rounded-xl bg-surface-2" />)}
            </div>
          )}
          {found && list.length === 0 && (
            <div data-testid="deposit-sheet-empty"><Note>{dt('deposits:takeaway.empty')}</Note></div>
          )}
          {found && list.length > 0 && (
            <div className="grid gap-3" data-testid="deposit-sheet-results">
              {customers.length > 1 && <Note>{dt('deposits:takeaway.sameCustomer')}</Note>}
              {customers.map(cid => {
                const group = list.filter(d => d.customer.id === cid);
                const who = group[0].customer;
                return (
                  <div key={cid} className="grid gap-2" role="group" aria-label={who.name || who.phone || dt('deposits:panel.noName')}>
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <span className="text-[15px] font-semibold text-t1">{who.name || dt('deposits:panel.noName')}</span>
                      {who.phone && <span className="text-[12.5px] text-t3">{who.phone}</span>}
                    </div>
                    <ul className="grid gap-2">
                      {group.map(d => {
                        const on = picked.some(p => p.id === d.id);
                        const row = rows.find(r => r.d.id === d.id);
                        const sameCount = rows.filter(r => r.d.customer.id === d.customer.id).length;
                        const sameOthers = rows.filter(r => r.d.customer.id === d.customer.id && r.d.id !== d.id)
                          .reduce((s, r) => s + Math.max(0, centsOf(r.p) || 0), 0);
                        const full = !on && capFor(toCents(d.available), totalCents, sameOthers) <= 0;
                        const tooMany = !on && sameCount >= MAX_DEPOSIT_USES;
                        const disabled = !d.usable || full || tooMany;
                        const reason = !d.usable ? (d.blocked ? dt(`deposits:blocked.${d.blocked}`) : dt('deposits:takeaway.notUsable'))
                          : full ? dt('deposits:takeaway.covered')
                            : tooMany ? dt('deposits:takeaway.maxUses', { count: MAX_DEPOSIT_USES }) : null;
                        const err = row ? rowError(row) : null;
                        return (
                          <li key={d.id} data-testid={`deposit-sheet-row-${d.id}`}
                            className={`overflow-hidden rounded-xl border-2 transition-colors ${on ? 'border-blue-600 bg-blue-600/5 dark:border-blue-400' : 'border-line bg-surface-1'} ${disabled ? 'opacity-60' : ''}`}>
                            <button type="button" aria-pressed={on} disabled={disabled} onClick={() => toggle(d)} data-testid={`deposit-sheet-pick-${d.id}`}
                              className="grid w-full min-h-[56px] grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 px-3.5 py-2.5 text-left disabled:cursor-not-allowed">
                              <span aria-hidden="true" className={`grid h-6 w-6 place-items-center rounded-md border-2 ${on ? 'border-blue-600 bg-blue-600 text-white dark:border-blue-400 dark:bg-blue-500' : 'border-line-strong'}`}>
                                {on && <Check className="h-4 w-4" />}
                              </span>
                              <span className="grid min-w-0 gap-0.5">
                                <span className="text-[15px] font-semibold text-t1">
                                  {methodName(d)}
                                  <span className="font-normal text-t3"> · {d.paid_at ? athensDateTime(d.paid_at) : ''}</span>
                                </span>
                                <span className="text-[12.5px] text-t3">
                                  {d.receipt && docNumber(d.receipt) ? `8.4 ${docNumber(d.receipt)}` : d.method === 'cash' ? dt('deposits:receipt.none') : ''}
                                  {d.note ? ` · ${d.note}` : ''}
                                </span>
                                {reason && (
                                  <span data-testid={`deposit-sheet-reason-${d.id}`}
                                    className={`text-[12.5px] font-semibold ${!d.usable ? 'text-amber-700 dark:text-amber-300' : 'text-t3'}`}>{reason}</span>
                                )}
                              </span>
                              <span className="grid justify-items-end">
                                <span className="text-[16px] font-bold tabular-nums text-t1">{dt('deposits:takeaway.available', { amount: money(d.available) })}</span>
                                {toCents(d.amount) !== toCents(d.available) && (
                                  <span className="text-[12px] text-t3">{dt('deposits:takeaway.of', { amount: money(d.amount) })}</span>
                                )}
                              </span>
                            </button>
                            {on && row && (
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-line px-3.5 py-2.5">
                                <label className="flex items-center gap-2">
                                  <span className="text-[13px] font-medium text-t2">{dt('deposits:takeaway.use')}</span>
                                  <input className={`${inputClass} h-11 w-28 text-right text-[17px] font-bold tabular-nums ${active === d.id ? 'border-blue-500' : ''}`}
                                    inputMode="decimal" autoComplete="off" value={row.p.text} aria-invalid={!!err}
                                    aria-label={dt('deposits:takeaway.amountFor', { method: methodName(d), amount: money(d.available) })}
                                    onFocus={() => setActive(d.id)} onChange={e => setAmount(d.id, e.target.value)}
                                    data-testid={`deposit-sheet-amount-${d.id}`} />
                                </label>
                                <span className={`text-[12.5px] ${err ? 'text-red-600 dark:text-red-400' : 'text-t3'}`}>
                                  {err || dt('deposits:takeaway.max', { amount: money(capOf(d) / 100) })}
                                </span>
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="grid content-start gap-2" role="group" aria-label={dt('deposits:takeaway.keypad')}>
          <span className="truncate text-[12.5px] text-t3" aria-live="polite">{activeLabel}</span>
          <div className="grid grid-cols-3 gap-2">
            {['1', '2', '3', '4', '5', '6', '7', '8', '9', ',', '0'].map(k => (
              <button key={k} type="button" className={keyClass} disabled={k === ',' && active === 'phone'}
                onClick={() => press(k)} data-testid={`deposit-key-${k === ',' ? 'comma' : k}`}>
                {k}
              </button>
            ))}
            <button type="button" className={`${keyClass} grid place-items-center`} aria-label={dt('deposits:takeaway.backspace')} onClick={() => press('back')}>
              <Delete className="h-5 w-5" />
            </button>
            <button type="button" className="col-span-3 h-10 rounded-xl text-[13px] font-medium text-t3 hover:bg-surface-2" onClick={() => press('clear')}>
              {dt('deposits:takeaway.clear')}
            </button>
          </div>
        </div>
      </div>

      <div role="status" aria-live="polite" data-testid="deposit-sheet-summary"
        className={`flex flex-wrap items-baseline justify-between gap-2 rounded-xl border px-4 py-3 ${appliedCents > 0 ? 'border-blue-500/40 bg-blue-500/8' : 'border-line bg-surface-2'}`}>
        <span className="text-[15px] font-semibold text-t1">
          {dt('deposits:takeaway.summary', { deposit: money(appliedCents / 100), due: money(dueCents / 100) })}
        </span>
        {appliedCents > 0 && dueCents === 0 && <span className="text-[13px] font-semibold text-blue-700 dark:text-blue-300">{dt('deposits:takeaway.coversAll')}</span>}
        {selectedCustomer && <span className="w-full text-[12.5px] text-t3">{selectedCustomer.name || selectedCustomer.phone}</span>}
      </div>
    </Shell>
  );
}
